/**
 * Standalone page entry — renders ChatPanel as a full-page chat
 * without FloatingButton or Shadow DOM.
 *
 * Used for hosted pages like m.mirlo.com/{org} where the client
 * shares a link instead of embedding a widget.
 */

import { useEffect } from 'preact/hooks';
import { ChatPanel } from './ChatPanel';
import {
  useChatState,
  type ChatMessage,
  type CaptureMode,
} from './useChatState';
import type {
  BrandingConfig,
  PreChatFormConfig,
  AvailabilityConfig,
  AttachmentsConfig,
} from '../client/api-types';
import type { QuickReplyOption } from './QuickReplies';

interface StandalonePageProps {
  branding: BrandingConfig;
  preChatForm: PreChatFormConfig | null;
  captureMode?: CaptureMode;
  quickReplies?: {
    style: 'chips' | 'list';
    options: QuickReplyOption[];
  } | null;
  verified?: boolean;
  headerStyle?: 'rich' | 'compact';
  bubbleStyle?: 'rounded' | 'classic';
  defaultTheme?: 'light' | 'dark';
  visitorId: string;
  sessionToken?: string;
  apiBaseUrl?: string;
  /** Transcript fetched by the standalone bootstrap after handshake. */
  initialMessages?: ChatMessage[];
  /**
   * Skip the real backend connection. Used by the preview mode so the
   * dashboard iframe stays visual-only: the greeting still injects, but
   * no socket is opened and messages don't flow.
   */
  demoMode?: boolean;
  /** Availability config from handshake. Absent = always online. */
  availability?: AvailabilityConfig;
  widgetId?: string;
  attachments?: AttachmentsConfig;
}

export function StandalonePage({
  branding,
  preChatForm,
  captureMode = 'pre_chat',
  quickReplies,
  verified = false,
  headerStyle = 'rich',
  bubbleStyle = 'rounded',
  defaultTheme = 'light',
  visitorId: _visitorId,
  sessionToken,
  apiBaseUrl,
  initialMessages,
  demoMode = false,
  availability,
  widgetId,
  attachments,
}: StandalonePageProps) {
  const [state, actions] = useChatState({
    branding,
    preChatForm,
    captureMode,
    demoMode,
    sessionToken,
    apiBaseUrl,
    initialMessages,
    availability,
    widgetId,
  });

  // Standalone is "always open" — the panel IS the page. The embedded flow
  // relies on FloatingButton to trigger `open()` (which connects the socket
  // and injects the greeting); here we do it on mount so the visitor sees
  // the greeting and can send messages without a prior interaction.
  useEffect(() => {
    actions.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div class="w-screen h-[100dvh] flex items-center justify-center bg-w-bg transition-[background] duration-[350ms]">
      <ChatPanel
        branding={branding}
        preChatForm={preChatForm}
        captureMode={captureMode}
        captureActive={state.captureActive}
        captureCanSkip={state.captureCanSkip}
        onCaptureSkip={actions.skipCaptureField}
        messages={state.messages}
        preChatDone={state.preChatDone}
        connectionStatus={state.connectionStatus}
        inputDisabled={state.inputDisabled}
        agentTyping={state.agentTyping}
        activeResponder={state.activeResponder}
        availabilityStatus={state.availabilityStatus}
        availability={availability}
        attachments={attachments}
        onUploadAttachment={actions.uploadAttachment}
        onClose={() => {}}
        onPreChatSubmit={actions.submitPreChat}
        onSendMessage={actions.sendMessage}
        onOfflineSubmit={actions.submitOfflineForm}
        offlineFormStatus={state.offlineFormStatus}
        offlineFormError={state.offlineFormError}
        onOfflineRetry={actions.resetOfflineForm}
        quickReplies={quickReplies}
        verified={verified}
        headerStyle={headerStyle}
        bubbleStyle={bubbleStyle}
        defaultTheme={defaultTheme}
        standalone={true}
      />
    </div>
  );
}
